import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "all";
  const sort = params.get("sort") || "featured";
  const tag = params.get("tag") || "";
  const search = params.get("search") || "";

  useEffect(() => { api.get("/products/categories").then((r) => setCats(r.data)); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category !== "all") qs.set("category", category);
    if (sort) qs.set("sort", sort);
    if (tag) qs.set("tag", tag);
    if (search) qs.set("search", search);
    qs.set("limit", "48");
    api.get(`/products?${qs}`).then((r) => setProducts(r.data.items)).finally(() => setLoading(false));
  }, [category, sort, tag, search]);

  useEffect(() => { load(); }, [load]);

  const setParam = (k, v) => { const p = new URLSearchParams(params); v && v !== "all" ? p.set(k, v) : p.delete(k); setParams(p); };

  const title = search ? `Search: "${search}"` : tag === "new" ? "New Arrivals" : tag === "best_sellers" ? "Best Sellers" : tag === "sale" ? "On Sale" : category !== "all" ? category : "All Products";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      <div className="text-center mb-10">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">The Collection</p>
        <h1 className="font-display text-4xl md:text-5xl mt-2" data-testid="shop-title">{title}</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2 justify-between border-y border-border py-4 mb-10">
        <div className="flex flex-wrap gap-2 items-center">
          <button data-testid="filter-all" onClick={() => { const p = new URLSearchParams(params); p.delete("category"); p.delete("tag"); setParams(p); }}
            className={`text-sm px-4 py-1.5 border transition ${category === "all" && !tag ? "bg-black text-white border-black dark:bg-white dark:text-black" : "border-border hover:border-[#D4AF37]"}`}>All</button>
          {cats.map((c) => (
            <button key={c.name} data-testid={`filter-${c.name.toLowerCase().replace(/\s/g,'-')}`} onClick={() => setParam("category", c.name)}
              className={`text-sm px-4 py-1.5 border transition ${category === c.name ? "bg-black text-white border-black dark:bg-white dark:text-black" : "border-border hover:border-[#D4AF37]"}`}>{c.name}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
          <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger data-testid="sort-select" className="w-44 rounded-none border-border"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
              <SelectItem value="rating">Top Rated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">{[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] bg-[#F5F1E8] dark:bg-[#141414] animate-pulse" />)}</div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="no-results"><p className="font-display text-2xl">No products found</p><Button onClick={() => setParams({})} className="mt-4 rounded-none bg-[#D4AF37] text-black">Clear filters</Button></div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10" data-testid="product-grid">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
